// ═══════════════════════════════════════════════════════════════
// usePushNotifications — owns the device's FCM token lifecycle.
//
// Responsibilities:
//   1. Detect whether the current browser supports web push at all
//   2. Surface the current Notification permission state
//      ('default' | 'granted' | 'denied' | 'unsupported')
//   3. enable() — request permission, register the service worker,
//      get an FCM token, persist it on users/{uid}/devices/{deviceId}
//   4. Refresh lastSeen on subsequent loads when already enabled
//   5. Foreground onMessage handler returns the latest payload so
//      callers can render an in-app toast (UI's choice)
//
// Why not auto-enable: per Brad's UX brief, default is OFF. The user
// has to opt in explicitly via Settings → "Enable notifications."
// No surprise permission prompts on first sign-in.
// ═══════════════════════════════════════════════════════════════
import { useEffect, useState, useCallback } from 'react';
import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, getFcmMessaging, VAPID_KEY, IS_SANDBOX } from '../firebase.js';

// Stable device ID derived from the FCM token tail. The full token can
// be 150+ chars and changes when refreshed; the last 20 chars are
// unique enough to avoid collisions between a user's own devices and
// short enough for a clean Firestore doc path. Bonus: when FCM rotates
// the token, the new last-20 differs so we get a fresh device doc
// instead of overwriting.
function deviceIdFromToken(token) {
  return token ? token.slice(-20) : null;
}

export function usePushNotifications() {
  const [permission, setPermission]  = useState(() => readPermission());
  const [token, setToken]            = useState(null);
  const [busy, setBusy]              = useState(false);
  const [error, setError]            = useState(null);
  const [lastPayload, setLastPayload] = useState(null);

  // Capability detection runs synchronously on mount; async messaging
  // support check happens in enable() so we can be precise about why
  // a browser is "unsupported."
  const browserSupportsBasics =
    typeof window !== 'undefined' &&
    typeof Notification !== 'undefined' &&
    'serviceWorker' in navigator;

  // Sandbox doesn't have its own FCM/SW pair — push is prod-only.
  // Calling enable() in sandbox would 404 the SW or grab a useless
  // token. Gate the whole capability flag accordingly.
  const supported = browserSupportsBasics && !IS_SANDBOX;

  const enable = useCallback(async () => {
    if (!supported) {
      setError(new Error(IS_SANDBOX
        ? 'Push notifications are prod-only — sandbox doesn\'t have them wired up.'
        : 'This browser doesn\'t support web push. On iPhone, install Bayless to your home screen first.'));
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      // 1. Permission. Must be triggered by a user gesture, which the
      //    caller is responsible for (Settings page button click).
      let perm = Notification.permission;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
      }
      setPermission(perm);
      if (perm !== 'granted') {
        if (perm === 'denied') {
          setError(new Error('Notifications were blocked. Re-enable in your browser/OS settings, then try again.'));
        }
        return false;
      }

      // 2. Service worker — must already exist at /firebase-messaging-sw.js
      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

      // 3. FCM token
      const messaging = await getFcmMessaging();
      if (!messaging) {
        setError(new Error('This browser version doesn\'t support FCM messaging.'));
        return false;
      }
      const fcmToken = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });
      if (!fcmToken) {
        setError(new Error('Could not register a push token. Try again, or check that notifications aren\'t blocked.'));
        return false;
      }
      setToken(fcmToken);

      // 4. Persist on the user's profile
      const uid = auth.currentUser?.uid;
      const deviceId = deviceIdFromToken(fcmToken);
      if (uid && deviceId) {
        await setDoc(
          doc(db, `users/${uid}/devices/${deviceId}`),
          {
            fcmToken,
            platform: 'web',
            userAgent: navigator.userAgent || '',
            createdAt: serverTimestamp(),
            lastSeen:  serverTimestamp(),
          },
          { merge: true }
        );
      }
      return true;
    } catch (e) {
      console.error('Enable notifications failed:', e);
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported]);

  // Disable: revoke the FCM token and remove the device doc for this
  // device. Permission stays at 'granted' — user can re-enable without
  // re-prompting unless they manually reset it in browser settings.
  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const messaging = await getFcmMessaging();
      const currentToken = token;
      if (messaging && currentToken) {
        try { await deleteToken(messaging); } catch (_) { /* best effort */ }
      }
      const uid = auth.currentUser?.uid;
      const deviceId = deviceIdFromToken(currentToken);
      if (uid && deviceId) {
        await deleteDoc(doc(db, `users/${uid}/devices/${deviceId}`)).catch(() => {});
      }
      setToken(null);
    } catch (e) {
      console.error('Disable notifications failed:', e);
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [token]);

  // If permission is already granted on app load (e.g., user enabled
  // notifications on a previous session), refresh the token + lastSeen
  // so dead-token cleanup on the server doesn't flag us as stale.
  useEffect(() => {
    if (!supported) return;
    if (permission !== 'granted') return;
    let cancelled = false;
    (async () => {
      try {
        const messaging = await getFcmMessaging();
        if (!messaging) return;
        const swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
          || await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        const t = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swReg,
        });
        if (cancelled || !t) return;
        setToken(t);
        const uid = auth.currentUser?.uid;
        const deviceId = deviceIdFromToken(t);
        if (uid && deviceId) {
          await setDoc(
            doc(db, `users/${uid}/devices/${deviceId}`),
            { fcmToken: t, platform: 'web', lastSeen: serverTimestamp() },
            { merge: true }
          );
        }
      } catch (_) { /* best effort — user can re-enable from Settings */ }
    })();
    return () => { cancelled = true; };
  }, [permission, supported]);

  // Foreground onMessage — caller can read lastPayload to show an
  // in-app toast. Background push goes through the service worker
  // and never lands here.
  useEffect(() => {
    if (!supported || permission !== 'granted') return;
    let unsub = () => {};
    (async () => {
      const messaging = await getFcmMessaging();
      if (!messaging) return;
      unsub = onMessage(messaging, (payload) => {
        setLastPayload({ ...payload, _receivedAt: Date.now() });
      });
    })();
    return () => unsub();
  }, [permission, supported]);

  return { permission, token, busy, error, supported, lastPayload, enable, disable };
}

// Browser-only Notification.permission read with a fallback for
// SSR / pre-mount renders (which shouldn't happen in this Vite SPA
// but defensive is cheap).
function readPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}
